import { useEffect, useState } from 'react';

import { useAuthentication } from '@givebacks/api';
import { GetAppExtensionCard, LinkYourCard, OnboardingLayout, UserAuthCard } from 'core/Components';
import { useBreakPoints } from 'core/Hooks';
import { useAtomValue } from 'jotai';
import { Box, ButtonMantine, Stack, TransitionElement } from 'ui';
import { useTransitionSections } from 'ui/Hooks';

import { donationCauseIdAtom, useDonationsCheckout, useStoreProvider } from '../../../Context';
import { useSimpleDonations } from '../../context/simpleDonations.provider';
import { PostDonationContinuationText } from './PostDonationContinuationText';
import { PostDonationSuccessCard } from './PostDonationSuccessCard';

type PostDonationBaseProps = {
  causeId?: string;
  setLayoutRef?: any; // This is going to be a PIA to type
  onComplete?: () => void;
};

interface NonAuthenticatedProps extends PostDonationBaseProps {
  step?: number | string;
  setStep?: (step: number | string) => void;
}

type PostDonationProps = NonAuthenticatedProps;

const PostDonationNonAuthenticated = ({ step, setStep, setLayoutRef }: NonAuthenticatedProps) => {
  const { cartCredentialAtom } = useStoreProvider();
  const { scrollIntoView, sections, handlers, scrollableRef, setTargetRef } = useTransitionSections(
    { card: false },
    4,
    true
  );
  const { useShowCart } = useDonationsCheckout();
  const { data: cart } = useShowCart();
  const causeId = cart?.cause_id;
  const { willSupportCause } = useSimpleDonations();
  const cartCredential = useAtomValue(cartCredentialAtom);

  const { isMobile } = useBreakPoints();

  const onPostDonationCardSuccess = () => {
    handlers.setItemProp(0, 'card', false);
    setTimeout(() => {
      handlers.setItemProp(1, 'mounted', true);
    }, 440);
  };

  const onCreateAccountComplete = () => {
    handlers.setItemProp(1, 'card', false);
    setTimeout(() => {
      handlers.setItemProp(2, 'mounted', true);
    }, 150);
  };

  const onLinkCardComplete = () => {
    setStep?.(2);
    handlers.setItemProp(3, 'mounted', true);
  };
  const onLinkCardSkip = () => {
    setStep?.(2);
    handlers.setItemProp(3, 'mounted', true);
  };
  useEffect(() => {
    setLayoutRef?.(scrollableRef);
  }, [scrollableRef.current]);

  useEffect(() => {
    if (cart?.recurring !== undefined) {
      if (!willSupportCause && !cart.recurring) {
        handlers.setItemProp(0, 'card', true);
      } else {
        onPostDonationCardSuccess();
      }
    }
    handlers.setItemProp(0, 'mounted', true);
  }, [cart?.recurring]);

  return (
    <Stack mih={'100vh'} gap="xl">
      <TransitionElement
        mounted={sections[0].mounted}
        onEntered={() => {
          setTargetRef(1);
        }}
      >
        <PostDonationSuccessCard
          causeId={causeId}
          onComplete={onPostDonationCardSuccess}
          isCardExpanded={sections[0].card}
        />
      </TransitionElement>
      <TransitionElement
        mounted={sections[1].mounted}
        targetRef={sections[1].targetRef}
        onEnter={scrollIntoView}
        onEntered={() => {
          handlers.setItemProp(1, 'card', true);
        }}
      >
        <TransitionElement mounted={isMobile}>
          <Box mb={'md'}>
            <PostDonationContinuationText step={step} />
          </Box>
        </TransitionElement>
        <UserAuthCard
          isCardExpanded={sections[1].card}
          initialEmail={cart?.email}
          onComplete={onCreateAccountComplete}
          cause_uuid={causeId}
        />
      </TransitionElement>

      <TransitionElement
        mounted={sections[2].mounted}
        onEnter={scrollIntoView}
        onEntered={() => {
          handlers.setItemProp(2, 'card', true);
        }}
        targetRef={sections[2].targetRef}
      >
        <LinkYourCard
          isCardExpanded={sections[2].card}
          credential={cartCredential ?? undefined}
          onComplete={onLinkCardComplete}
          onSkip={onLinkCardSkip}
        />
      </TransitionElement>

      <TransitionElement
        mounted={sections[3].mounted}
        onEnter={scrollIntoView}
        onEntered={() => {
          handlers.setItemProp(3, 'card', true);
        }}
        targetRef={sections[3].targetRef}
      >
        <GetAppExtensionCard causeId={causeId} isCardExpanded={sections[3].card} />
      </TransitionElement>
      <Box mih={'50vh'} />
    </Stack>
  );
};

export const PostDonationsAuthenticated = ({ causeId, setLayoutRef }: PostDonationProps) => {
  const { cartCredentialAtom } = useStoreProvider();
  const { scrollIntoView, sections, handlers, scrollableRef } = useTransitionSections(
    { card: false },
    2,
    true
  );
  const { onLastStepComplete } = useSimpleDonations();

  const cartCredential = useAtomValue(cartCredentialAtom);
  const handleComplete = () => {
    handlers.setItemProp(1, 'mounted', true);
  };

  useEffect(() => {
    setLayoutRef?.(scrollableRef);
  }, [scrollableRef.current]);

  return (
    <Stack gap="xl">
      <TransitionElement mounted={true}>
        <PostDonationSuccessCard causeId={causeId} isCardExpanded={false} />
      </TransitionElement>

      <TransitionElement
        mounted={true}
        onEnter={scrollIntoView}
        onEntered={() => {
          handlers.setItemProp(0, 'card', true);
        }}
        targetRef={sections[0].targetRef}
      >
        <LinkYourCard
          isCardExpanded={true}
          credential={cartCredential}
          onComplete={handleComplete}
          onSkip={onLastStepComplete}
        />
      </TransitionElement>

      <TransitionElement mounted={sections[1].mounted}>
        <ButtonMantine onClick={onLastStepComplete} fullWidth>
          Finish
        </ButtonMantine>
      </TransitionElement>
    </Stack>
  );
};

const PostDonationsStepsWrapper = (props: PostDonationProps) => {
  const { isAuthenticated } = useAuthentication();
  const [initiallyAuthenticated] = useState(isAuthenticated);

  return initiallyAuthenticated ? (
    <PostDonationsAuthenticated {...props} />
  ) : (
    <PostDonationNonAuthenticated {...props} />
  );
};

// Displays the cause description next to the simpleDonation
const PostDonation = () => {
  const donationCauseId = useAtomValue(donationCauseIdAtom);
  const [layoutRef, setLayoutRef] = useState(null);

  const [step, setStep] = useState<string | number>(1);

  return (
    <OnboardingLayout
      layoutRef={layoutRef}
      leftSideContent={<PostDonationContinuationText step={step} />}
    >
      <PostDonationsStepsWrapper
        causeId={donationCauseId}
        step={step}
        setStep={setStep}
        setLayoutRef={setLayoutRef}
      />
    </OnboardingLayout>
  );
};

export { PostDonation, PostDonationNonAuthenticated, PostDonationsStepsWrapper };
